import type { CardDefId, GameEvent, PlayerId, Side } from '@cards/engine';
import { getCardPlaybackLabel, getCardPresentation } from '@cards/cards';
import { cellDisplayLabel } from './locale.js';
import type { PlaybackNotice } from './TurnPlayback.js';

export type PublicPlaybackKind = PlaybackNotice['kind'];

export interface PublicPlaybackFormatOptions {
  readonly labelPlayer?: (playerId: PlayerId | 'draw') => string;
  /** The viewer's side, so a cell reads «ваша сторона» / «сторона соперника». */
  readonly viewerSide?: Side;
}

export interface PublicPlaybackLabel {
  readonly text: string;
  readonly kind: PublicPlaybackKind;
  readonly ownerId?: PlayerId;
}

const FIZZLE_REASONS: Readonly<Record<string, string>> = {
  target_invalid: 'цель больше недоступна',
  invalid_target: 'недопустимая цель',
  destination_unavailable: 'ячейка назначения недоступна',
  duplicate_armed: 'такая Реакция уже взведена',
  source_unavailable: 'карта больше недоступна',
  attachment_not_created: 'Вирус не удалось прикрепить',
  already_captured: 'карта уже захвачена',
  wrong_location: 'цель в другой локации',
  host_left: 'носитель покинул поле',
  target_left: 'цель покинула поле',
  no_target: 'нет допустимой цели',
};

function playerLabel(
  playerId: PlayerId | 'draw',
  options: PublicPlaybackFormatOptions,
): string {
  return options.labelPlayer?.(playerId) ?? (playerId === 'draw' ? 'ничья' : 'игрок');
}

function cellLabel(cellId: string, options: PublicPlaybackFormatOptions): string {
  return cellDisplayLabel(cellId, options.viewerSide);
}

function cardLabel(defId: CardDefId): string {
  return getCardPresentation(defId)?.name ?? 'карта';
}

function zoneLabel(zone: string): string {
  switch (zone) {
    case 'deck': return 'колода';
    case 'hand': return 'рука';
    case 'board': return 'поле';
    case 'discard': return 'сброс';
    case 'armed': return 'взведённые реакции';
    case 'attachment': return 'прикреплённые карты';
    default: return 'неизвестная зона';
  }
}

function statLabel(stat: string): string {
  switch (stat) {
    case 'attack': return 'Атака';
    case 'health': return 'Здоровье';
    case 'maxHealth': return 'Макс. здоровье';
    default: return 'Характеристика';
  }
}

function phaseLabel(phase: string): string {
  return phase === 'planning' ? 'планирование' : phase === 'resolution' ? 'разыгрывание' : 'неизвестная фаза';
}

function lifecycleResolved(kind: 'FIELD' | 'SPELL' | 'REACTION' | 'VIRUS'): string {
  switch (kind) {
    case 'FIELD': return 'КАРТА РАЗРЕШЕНА';
    case 'SPELL': return 'ЗАКЛИНАНИЕ РАЗРЕШЕНО';
    case 'REACTION': return 'РЕАКЦИЯ РАЗРЕШЕНА';
    case 'VIRUS': return 'ВИРУС РАЗРЕШЁН';
  }
}

function lifecycleFizzled(kind: 'FIELD' | 'SPELL' | 'REACTION' | 'VIRUS'): string {
  switch (kind) {
    case 'FIELD': return 'Карта не сработала';
    case 'SPELL': return 'Заклинание не сработало';
    case 'REACTION': return 'Реакция не сработала';
    case 'VIRUS': return 'Вирус не сработал';
  }
}

function lifecycleNoticeKind(
  kind: 'FIELD' | 'SPELL' | 'REACTION' | 'VIRUS',
): PublicPlaybackKind {
  return kind === 'SPELL' ? 'spell' : kind === 'REACTION' ? 'reaction' : kind === 'VIRUS' ? 'virus' : 'phase';
}

function fizzleReason(reason: string): string {
  return FIZZLE_REASONS[reason] ?? 'эффект не может быть применён';
}

function damageTargetLabel(
  target: Extract<GameEvent, { type: 'DamageDealt' }>['target'],
  targetCellId: string | undefined,
  options: PublicPlaybackFormatOptions,
): string {
  return target.kind === 'player'
    ? playerLabel(target.playerId, options)
    : targetCellId === undefined ? 'карта' : `карта (${cellLabel(targetCellId, options)})`;
}

function hackCandidateLabel(
  candidate: Extract<GameEvent, { type: 'HackTriggered' }>['selected'],
  options: PublicPlaybackFormatOptions,
): string {
  return candidate.target.kind === 'player'
    ? playerLabel(candidate.target.playerId, options)
    : candidate.targetCellId === undefined ? 'карта' : cellLabel(candidate.targetCellId, options);
}

/**
 * Returns a definition only when it is carried by the already-public event
 * payload. It deliberately never resolves an InstanceId through GameState.
 */
export function publicEventCardDefId(event: GameEvent): CardDefId | undefined {
  switch (event.type) {
    case 'CardDrawn': return event.sourceDefId;
    case 'AttachmentAdded': return event.attachment.defId;
    case 'EffectAdded': return event.effect.defId;
    case 'EffectCounterChanged': return event.defId;
    case 'CardsAbsorbed': return event.receiver.defId;
    case 'UnitDied':
    case 'TokenRemoved':
    case 'TokenCreated':
    case 'TokenCreationSkipped':
    case 'CardConsumed':
    case 'CardCreated':
      return event.defId;
    case 'CardTransformed': return event.toDefId;
    default: return undefined;
  }
}

/** Event-only ownership used for log coloring; unknown ownership stays neutral. */
export function publicEventOwner(event: GameEvent): PlayerId | undefined {
  switch (event.type) {
    case 'CardPlanned':
    case 'CardUnplanned':
    case 'PlacementStaged':
    case 'PlacementUnstaged':
    case 'PlayerReadyChanged':
    case 'ResourcesChanged':
    case 'PlayerHealthRestored':
    case 'CardDrawn':
    case 'CardRevealed':
    case 'CardResolved':
    case 'CardFizzled':
    case 'ReactionArmed':
    case 'ReactionTriggered':
    case 'TemporaryEnergyGranted':
    case 'TemporaryEnergyExpired':
      return event.playerId;
    case 'AttachmentAdded': return event.attachment.ownerId;
    case 'AttachmentRemoved': return event.ownerId;
    case 'CaptureApplied': return event.newControllerId;
    case 'CaptureReturned': return event.restoredControllerId;
    case 'CaptureFizzled': return event.newControllerId;
    case 'HackApplied': return event.marker.initiatorPlayerId;
    case 'HackTriggered': return event.initiatorPlayerId;
    case 'TokenCreated':
    case 'TokenCreationSkipped':
    case 'TokenRemoved':
    case 'CardConsumed':
    case 'AssemblyCompleted':
    case 'CardCreated':
      return event.ownerId;
    case 'CardsAbsorbed': return event.receiver.controllerId;
    case 'UnitDied': return event.controllerIdAtDeath ?? event.ownerId;
    case 'DamageDealt': return event.target.kind === 'player' ? event.target.playerId : undefined;
    case 'EffectAdded': return event.effect.scope.kind === 'player' ? event.effect.scope.playerId : undefined;
    case 'EffectCounterChanged': return event.scope.kind === 'player' ? event.scope.playerId : undefined;
    case 'MatchEnded': return event.winner === 'draw' ? undefined : event.winner;
    default: return undefined;
  }
}

function defaultKind(event: GameEvent): PublicPlaybackKind {
  switch (event.type) {
    case 'TokenCreated':
    case 'TokenCreationSkipped':
    case 'CardCreated': return 'creation';
    case 'CardConsumed':
    case 'CardsAbsorbed':
    case 'CardTransformed':
    case 'AssemblyCompleted': return 'assembly';
    case 'TemporaryEnergyGranted':
    case 'TemporaryEnergyExpired':
    case 'CardDrawn':
    case 'ResourcesChanged': return 'resource';
    case 'PlayerHealthRestored':
    case 'UnitHealed': return 'resource';
    case 'TimedDestroyScheduled':
    case 'TimedDestroyResolved': return 'timed';
    case 'HackApplied':
    case 'HackTriggered':
    case 'HackRemoved': return 'hack';
    case 'ReactionArmed':
    case 'ReactionTriggered': return 'reaction';
    case 'AttachmentAdded':
    case 'AttachmentTriggered':
    case 'AttachmentRemoved': return 'virus';
    case 'CaptureApplied':
    case 'CaptureReturned':
    case 'CaptureFizzled': return 'capture';
    case 'AttackPerformed':
    case 'AttackAvoidanceChecked':
    case 'AttackResolved':
    case 'ExtraAttackQueued':
    case 'ExtraAttackResolved':
    case 'HeatChanged':
    case 'FreezeChanged':
    case 'DamageDealt':
    case 'ShieldAbsorbed':
    case 'ShieldDepleted': return 'attack';
    case 'RandomDamageReplacementResolved': return 'attack';
    case 'RandomUnitSelectionResolved': return 'phase';
    case 'CardRevealed':
    case 'CardResolved':
    case 'CardFizzled': return lifecycleNoticeKind(event.kind);
    default: return 'phase';
  }
}

function defaultText(event: GameEvent, options: PublicPlaybackFormatOptions): string {
  switch (event.type) {
    case 'CardPlanned': return 'Карта добавлена в план';
    case 'CardUnplanned': return 'Карта убрана из плана';
    case 'PlacementStaged': return `Выкладка запланирована: ${cellLabel(event.placement.cellId, options)}`;
    case 'PlacementUnstaged': return `Выкладка отменена: ${cellLabel(event.cellId, options)}`;
    case 'PlayerReadyChanged': return event.ready ? 'Игрок готов' : 'Игрок не готов';
    case 'ResourcesChanged': return `Энергия: ${event.energy}/${event.energyMax}`;
    case 'PlayerHealthRestored':
      return `Здоровье восстановлено на ${event.amount} · ${event.current}`;
    case 'EffectDurationChanged': return `Длительность эффекта → ${event.duration}`;
    case 'RoundStarted': return `Раунд ${event.round} начался`;
    case 'RoundEnded': return `Раунд ${event.round} завершён`;
    case 'PhaseChanged': return `Фаза → ${phaseLabel(event.phase)}`;
    case 'CardDrawn': return event.sourceDefId === undefined
      ? 'Карта взята из колоды'
      : `Добор карты · источник: ${cardLabel(event.sourceDefId)}`;
    case 'CardRevealed': {
      const prefix = event.kind === 'FIELD'
        ? 'РАСКРЫТИЕ'
        : event.kind === 'SPELL'
          ? 'ЗАКЛИНАНИЕ РАСКРЫТО'
          : event.kind === 'REACTION'
            ? 'РЕАКЦИЯ РАСКРЫТА'
            : 'ВИРУС РАСКРЫТ';
      return `${prefix} · источник: ${playerLabel(event.playerId, options)}`;
    }
    case 'CardResolved':
      return event.kind === 'SPELL'
        ? `${lifecycleResolved(event.kind)} · эффект завершён · карта в сбросе`
        : lifecycleResolved(event.kind);
    case 'CardFizzled': return `FAIL · ${lifecycleFizzled(event.kind)}: ${fizzleReason(event.reason)}`;
    case 'ReactionArmed': return 'РЕАКЦИЯ ВЗВЕДЕНА';
    case 'ReactionTriggered': return `РЕАКЦИЯ СРАБОТАЛА · итоговый урон ${event.amount}`;
    case 'UnitPlaced': return `Карта размещена на поле: ${cellLabel(event.cellId, options)}`;
    case 'UnitEnteredPlay': return `Появление: ${cellLabel(event.cellId, options)}`;
    case 'UnitAppeared':
      return `Появление завершено: ${cellLabel(event.cellId, options)}${event.outcome === 'fizzled' ? ' · не состоялось' : ''}`;
    case 'UnitHealed': return `Лечение +${event.amount} · ${event.previous} → ${event.current}`;
    case 'FieldRevealCompleted': return `Раскрытие полевых карт завершено · раунд ${event.round}`;
    case 'HeatChanged':
      return event.reason === 'cooled'
        ? `ОХЛАЖДЕНИЕ · ${event.previous} → ${event.current}`
        : `ПЕРЕГРЕВ · ${event.previous} → ${event.current}`;
    case 'FreezeChanged':
      return event.reason === 'thawed'
        ? `РАЗМОРОЗКА · ${event.previous} → ${event.current}`
        : event.reason === 'combat_skip'
          ? `ЗАМОРОЗКА · бой пропущен, осталось ${event.current}`
          : `ЗАМОРОЗКА · ${event.previous} → ${event.current}`;
    case 'ShieldAbsorbed': return `Щит поглотил ${event.amount}, осталось ${event.remaining}`;
    case 'ShieldDepleted': return 'Щит полностью израсходован';
    case 'AttackPerformed':
      return `Атака → ${damageTargetLabel(event.target, event.targetCellId, options)}`;
    case 'AttackAvoidanceChecked':
      return event.succeeded
        ? `УКЛОНЕНИЕ · ${cellLabel(event.targetCellId, options)} · атака не попала в цель`
        : `УКЛОНЕНИЕ · ${cellLabel(event.targetCellId, options)} · проверка не сработала`;
    case 'AttackResolved':
      return event.avoided === true
        ? `Атака завершена · ${cellLabel(event.targetCellId, options)} · уклонение · урон 0`
        : `Атака завершена · ${cellLabel(event.targetCellId, options)} · итог ${event.amount}${event.parts.length > 1 ? ` · частей ${event.parts.length}` : ''}`;
    case 'UnitMoved': return `Боец перемещён: ${cellLabel(event.from, options)} → ${cellLabel(event.to, options)}`;
    case 'AttachmentAdded': return `${cardLabel(event.attachment.defId)} · Вирус прикреплён`;
    case 'AttachmentTriggered': return 'ВИРУС СРАБОТАЛ';
    case 'AttachmentRemoved': return 'Вирус удалён';
    case 'CaptureApplied':
      return `ЗАХВАТ · перенос ${cellLabel(event.fromCellId, options)} → ${cellLabel(event.toCellId, options)} · контроль → ${playerLabel(event.newControllerId, options)}`;
    case 'CaptureReturned':
      return event.reason === 'expired'
        ? `ВОЗВРАТ · ${event.fromCellId === undefined ? 'поле' : cellLabel(event.fromCellId, options)} → ${cellLabel(event.toCellId, options)} · контроль: ${playerLabel(event.restoredControllerId, options)}`
        : 'ЗАХВАТ ЗАВЕРШЁН · карта покинула поле';
    case 'CaptureFizzled': return `ЗАХВАТ НЕ СРАБОТАЛ · ${fizzleReason(event.reason)}`;
    case 'ExtraAttackQueued': return 'Подготовлена дополнительная атака';
    case 'ExtraAttackResolved': return event.performed ? 'ДОПОЛНИТЕЛЬНАЯ АТАКА' : 'ДОПОЛНИТЕЛЬНАЯ АТАКА · не выполнена';
    case 'CardMoved': return `Карта: ${zoneLabel(event.from)} → ${zoneLabel(event.to)}`;
    case 'CombatStarted': return `Бой начался (раунд ${event.round})`;
    case 'DamageDealt': {
      const beforeShield = event.amount + (event.absorbed ?? 0);
      const details = [
        event.combat === true ? 'обычная атака в бою' : 'эффект карты',
        ...(event.baseAmount !== undefined && event.baseAmount !== beforeShield
          ? [`исходно ${event.baseAmount}, после модификаторов ${beforeShield}`]
          : []),
        ...(event.absorbed === undefined ? [] : [`Щит поглотил ${event.absorbed}`]),
        ...(event.lethal === true ? ['смертельный урон'] : []),
      ];
      return `Урон → ${damageTargetLabel(event.target, event.targetCellId, options)} · итог ${event.amount} · ${details.join(' · ')}`;
    }
    case 'HackApplied': return 'ВЗЛОМ · следующая атака будет перенаправлена';
    case 'HackTriggered':
      return `ВЗЛОМ · кандидаты: ${event.candidates.map((candidate) => hackCandidateLabel(candidate, options)).join('; ')} · выбрана цель: ${hackCandidateLabel(event.selected, options)}`;
    case 'HackRemoved': {
      const reason = event.reason === 'replaced'
        ? 'заменён новым'
        : event.reason === 'triggered'
          ? 'сработал'
          : event.reason === 'controller_changed'
            ? 'сменился контроллер'
            : 'снят эффектом';
      return `Взлом снят: ${reason}`;
    }
    case 'StatChanged': return `${statLabel(event.stat)} ${event.delta >= 0 ? '+' : ''}${event.delta}`;
    case 'EffectAdded': return `Добавлен эффект: ${cardLabel(event.effect.defId)}`;
    case 'EffectCounterChanged':
      return `Счётчик эффекта ${cardLabel(event.defId)} ${event.delta >= 0 ? '+' : ''}${event.delta} · ${event.previous} → ${event.current}`;
    case 'EffectRemoved': return 'Эффект снят';
    case 'UnitDied':
      return event.cause === 'timed'
        ? `Уничтожение по таймеру: ${cardLabel(event.defId)}`
        : `Боец погиб: ${cardLabel(event.defId)} · ${cellLabel(event.cellId, options)}`;
    case 'TokenRemoved': return `Токен убран с поля: ${cardLabel(event.defId)}`;
    case 'TokenCreated': return event.cellId === undefined
      ? `Создана карта в руке: ${cardLabel(event.defId)}`
      : `Создан токен: ${cardLabel(event.defId)}`;
    case 'TokenCreationSkipped': {
      const reason = event.reason === 'location_full'
        ? 'нет свободной ячейки'
        : event.reason === 'reserved'
          ? 'ячейка зарезервирована'
          : 'ячейка занята';
      const destination = event.cellId !== undefined ? cellLabel(event.cellId, options) : (event.tableId === undefined ? undefined : `локация ${event.tableId + 1}`);
      return `Токен ${cardLabel(event.defId)} не создан${destination === undefined ? '' : ` · ${destination}`}: ${reason}`;
    }
    case 'CardConsumed': return `Поглощено: ${cardLabel(event.defId)} · не считается гибелью`;
    case 'CardsAbsorbed': {
      const donors = event.donors.map((donor) => cardLabel(donor.defId)).join(', ');
      return `ПОГЛОЩЕНИЕ · ${cardLabel(event.receiver.defId)} поглощает ${event.donors.length}: ${donors} · атака +${event.totals.attack} · здоровье +${event.totals.health}`;
    }
    case 'CardTransformed': return `Превращение: ${cardLabel(event.fromDefId)} → ${cardLabel(event.toDefId)}`;
    case 'AssemblyCompleted': return `СБОРКА · 2 Фрагмента → Восстановленный робот · локация ${event.tableId + 1}`;
    case 'TemporaryEnergyGranted': return `Временная энергия +${event.amount} в раунде ${event.availableRound}`;
    case 'TemporaryEnergyExpired': return `Временная энергия: потрачено ${event.spent}, исчезло ${event.unused}`;
    case 'TimedDestroyScheduled': return `Отложенное уничтожение · карт: ${event.marker.instanceIds.length} · раунд ${event.marker.deadlineRound}`;
    case 'TimedDestroyResolved': return event.instanceId === undefined
      ? `Отложенное уничтожение пропущено в раунде ${event.resolvedRound}: карта уже ушла`
      : `Отложенное уничтожение сработало в раунде ${event.resolvedRound}`;
    case 'CardCreated': return `Создана карта в руке: ${cardLabel(event.defId)}`;
    case 'RuleChanged': return `Правило ${event.key} = ${String(event.value)}`;
    case 'VarChanged': return 'Переменная карты изменена';
    case 'RandomDamageReplacementResolved': {
      const mechanic = event.id === 'dodge' ? 'УКЛОНЕНИЕ' : 'СЛУЧАЙНАЯ ЗАМЕНА УРОНА';
      const chance = `${Math.round(event.probability * 100)}%`;
      if (!event.succeeded) return `${mechanic} · не сработало (${chance}) · урон ${event.original.amount}`;
      const result = event.result === null ? 'урон предотвращён' : `урон ${event.original.amount} → ${event.result.amount}`;
      return `${mechanic} · успех (${chance}) · ${result}`;
    }
    case 'RandomUnitSelectionResolved':
      return `СЛУЧАЙНЫЙ ВЫБОР · кандидаты: ${event.candidates.map(({ cellId }) => cellLabel(cellId, options)).join('; ')} · выбрана: ${cellLabel(event.selected.cellId, options)}`;
    case 'CombatEnded': return `Бой завершён (раунд ${event.round})`;
    case 'MatchEnded': return `Матч завершён — победитель: ${playerLabel(event.winner, options)}`;
    default: return 'Событие';
  }
}

/**
 * Pure public formatter. Its inputs contain no GameState, registry instances,
 * zones or RNG; card-specific metadata is selected only by a defId that the
 * event payload itself already exposes.
 */
export function formatPublicPlaybackEvent(
  event: GameEvent,
  options: PublicPlaybackFormatOptions = {},
): PublicPlaybackLabel {
  const defId = publicEventCardDefId(event);
  const authored = defId === undefined ? undefined : getCardPlaybackLabel(defId, event.type);
  const ownerId = publicEventOwner(event);
  return {
    text: authored?.label ?? defaultText(event, options),
    kind: authored?.kind ?? defaultKind(event),
    ...(ownerId === undefined ? {} : { ownerId }),
  };
}
